<template>
  <div class="card-base card-round bg-dynamic px-4 py-8 lg:p-10">
    <FormKit
      :id="formId"
      v-slot="{disabled, state: {loading}}"
      type="form"
      form-class="space-y-6"
      :actions="false"
      @input="formChanged"
      @submit="handleSubmittingApplication"
    >
      <h2 class="text-2xl font-medium text-white lg:text-3xl">
        Solliciteer direct
      </h2>
      <div class="space-y-2 lg:gap-y-4 lg:space-y-4">
        <FormKit
          label="Voornaam"
          name="first_name"
          type="text"
          :floating-label="true"
          validation="required"
          validation-label="Voornaam"
        />
        <FormKit
          label="Achternaam"
          name="last_name"
          type="text"
          :floating-label="true"
          validation="required"
          validation-label="Achternaam"
        />
      </div>
      <div class="space-y-2 lg:space-y-4">
        <FormKit
          label="Telefoon"
          name="phone"
          type="text"
          :floating-label="true"
          validation="required"
          validation-label="Telefoon"
        />

        <FormKit
          label="E-mailadres"
          name="email"
          type="email"
          :floating-label="true"
          validation="required|email"
        />
      </div>
      <FormKit
        v-if="!vacancy"
        type="select"
        name="team"
        label="Vestiging"
        input-class="bg-transparent text-white placeholder:text-white"
        label-class="!bg-dynamic !text-white"
        placeholder=" "
        validation="required"
        :floating-label="true"
        :options="branchOptions"
      />
      <div class="space-y-2 lg:space-y-4">
        <FormKit
          label="Upload je CV"
          name="resume"
          type="file"
          validation-label="CV"
        >
          <template #noFiles="context">
            <div :class="context.classes.noFiles">
              Bladeren
            </div>
          </template>
        </FormKit>
      </div>

      <FormKit
        wrapper-class="flex gap-2"
        label="Ik ga akkoord met de Privacyvoorwaarden"
        name="privacy"
        type="checkbox"
        validation="required|accepted"
        validation-label="Privacyvoorwaarden"
      >
        <template #label>
          <span class="text-white">
            Ik ga akkoord met de <NuxtLink
              to="/privacy"
              target="_blank"
              class="underline"
            >privacyvoorwaarden</NuxtLink>.*
          </span>
        </template>
      </FormKit>

      <FormKit
        wrapper-class="flex gap-2"
        label="Ik wil op de hoogte blijven van de nieuwste vacatures en ontwikkelingen per mail of app"
        name="mailings"
        type="checkbox"
      >
        <template #label>
          <span class="text-white">
            Ik wil op de hoogte blijven van de nieuwste vacatures en ontwikkelingen per mail of app.
          </span>
        </template>
      </FormKit>
      <FormKit
        type="submit"
        :disabled="disabled"
      >
        {{ loading ? 'Bezig met verzenden...' : 'Sollicitatie versturen' }}
      </FormKit>
      <p
        v-if="extraErrors"
        class="text-error"
      >
        {{ extraErrors }}
      </p>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import type {FormKitGroupValue, FormKitNode} from '@formkit/core';
import {useGtm} from '@gtm-support/vue-gtm';
import {
  type MutationSubmitApplicationArgs,
  SubmitApplicationDocument,
  type SubmitApplicationMutation,
  useBranchQuery,
  type Vacancy,
  type Vestiging,
} from '~/graphql/graphql';

defineEmits<{(e: 'submit', data: FormKitGroupValue): void }>();

const props = defineProps<{
  vacancy?: Vacancy
  recruiterId?: number,
  formId: string,
}>();

const extraErrors = ref();

const router = useRouter();

const {result: branchResult} = useBranchQuery();
const branches = computed<Vestiging[]>(() => branchResult.value?.branch ?? []);

const branchOptions = computed(() => {
  return branches.value.map((branch) => {
    return {
      value: branch.team,
      label: branch.title,
    };
  });
});

const query = useRoute().query;

const {mutate, onDone, onError} = useSofieMutation<SubmitApplicationMutation>(SubmitApplicationDocument, () => ({
  variables: {
    vacancy_id: props.vacancy?.id || null,
    utm: {
      utm_campaign: query.utm_campaign,
      utm_content: query.utm_content,
      utm_medium: query.utm_medium,
      utm_source: query.utm_source || 'direct',
      utm_term: query.utm_term,
    },
  },
}));

onError((e) => {
  if (e?.graphQLErrors[0]?.message && e.graphQLErrors[0].message !== 'validation') {
    extraErrors.value = e.graphQLErrors[0].message;
  }
});

onDone(({data}) => {
  extraErrors.value = null;

  formSubmitted();

  router.push({
    name: 'bedankt',
    query: {
      n: data?.submitApplication?.first_name,
      recruiter: props.recruiterId,
      hideProcess: 'true',
    },
  });
});

async function handleSubmittingApplication(formData: MutationSubmitApplicationArgs, form: FormKitNode) {
  await mutate({
    first_name: formData.first_name,
    last_name: formData.last_name,
    phone: formData.phone,
    email: formData.email,
    resume: formData.resume[0]?.file,
    mailings: formData.mailings || false,
    team: formData.team || null,
  }, form);
}

const eventSent = ref(false);

function formChanged() {
  if (!eventSent.value) {
    formStarted();
    eventSent.value = true;
  }
}

function formStarted() {
  const gtm = useGtm();
  gtm?.trackEvent({
    event: 'add_to_cart',
    ecommerce: {
      items: [
        {
          item_name: props.vacancy?.title ?? 'Open sollicitatie',
          item_id: props.vacancy?.id,
          item_brand: props.vacancy?.client || 'Unknown',
          item_category: props.vacancy?.location?.title || props.vacancy?.city || 'Unknown',
          item_category2: props.vacancy?.sector?.title || 'Unknown',
          item_category3: props.vacancy?.recruiter?.department?.title || 'Unknown',
        },
      ],
    },
  });
}

function formSubmitted() {
  const gtm = useGtm();
  gtm?.trackEvent({
    event: 'purchase',
    ecommerce: {
      items: [
        {
          item_name: props.vacancy?.title ?? 'Open sollicitatie',
          item_id: props.vacancy?.id,
          item_brand: props.vacancy?.client || 'Unknown',
          item_category: props.vacancy?.location?.title || props.vacancy?.city || 'Unknown',
          item_category2: props.vacancy?.sector?.title || 'Unknown',
          item_category3: props.vacancy?.recruiter?.department?.title || 'Unknown',
        },
      ],
    },
  });
}
</script>
